"use client";
import React, { useRef } from 'react';

const sustainabilityResults = [
  {
    image: '/images/portfolio/portfolio-5.jpg',
    tags: ['Cosmetics', 'Label Type'],
    title: 'Premium Look, Eco-Friendly',
    description: 'Achieved luxury finishes using water-based coatings instead of metallic foils.',
  },
  {
    image: '/images/portfolio/portfolio-1.jpg',
    tags: ['FMCG', 'Label Type'],
    title: 'Dairy Packaging',
    description: 'Reduced plastic usage by 30% by switching to recyclable PP-based labels.',
  },
  {
    image: '/images/portfolio/portfolio-2.jpg',
    tags: ['Liquor', 'Label Type'],
    title: 'Responsible Packaging',
    description:
      'Introduced tamper-proof labels with recyclable substrates for a sustainable upgrade.',
  },
  {
    image: '/images/portfolio/portfolio-3.jpg', // Added items
    tags: ['Pharma', 'Label Type'],
    title: 'Sustainable Integrity',
    description: 'Digital printing on certified compostable materials for sensitive medical products.',
  },
  {
    image: '/images/portfolio/portfolio-4.jpg',
    tags: ['Food', 'Label Type'],
    title: 'Zero Waste Labels',
    description: 'Switching to wash-off adhesive technology to enable 100% bottle recyclability.',
  },
];

const SustainabilityResultsSection = () => {
  const scrollContainerRef = useRef(null);

  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = 400; // Card width + gap approx
      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  return (
    <section className="w-full bg-[#F1F3F6] pt-16 pb-32 sm:pb-40 md:pb-52 overflow-hidden">
      <div className="max-w-[1216px] mx-auto px-6 md:px-8">
        <div className="flex flex-col gap-8">
          {/* Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
            <div>
              <h2 className="text-[32px] md:text-[40px] font-bold text-[#112140] mb-2 font-display">
                Real Impact, Real Results
              </h2>
              <p className="text-lg text-[#475467] max-w-2xl">
                Discover how our sustainable label solutions help brands cut waste, stay compliant, and create packaging for a better future.
              </p>
            </div>
            <div className="flex gap-[24px] mt-4 sm:mt-0">
              <button
                onClick={() => scroll('left')}
                className="w-[48px] h-[48px] border-2 border-[#2E8B8D] rounded-full shadow p-[12px] hover:bg-white transition-all duration-200 active:scale-95 group"
              >
                <img src="/images/img_icon_iconoir.svg" alt="Previous" className="w-full h-full group-hover:opacity-80 transition-opacity" />
              </button>
              <button
                onClick={() => scroll('right')}
                className="w-[48px] h-[48px] border-2 border-[#2E8B8D] rounded-full shadow p-[12px] hover:bg-white transition-all duration-200 active:scale-95 group"
              >
                <img src="/images/img_icon_jam_icons.svg" alt="Next" className="w-full h-full group-hover:opacity-80 transition-opacity" />
              </button>
            </div>
          </div>

          {/* Cards - Scrollable Container */}
          <div
            ref={scrollContainerRef}
            className="flex gap-8 overflow-x-auto pb-4 scrollbar-hide snap-x snap-mandatory"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {sustainabilityResults.map((card, idx) => (
              <div
                key={idx}
                className="w-[320px] md:w-[365px] flex-shrink-0 bg-white rounded-[16px] shadow-[0_6px_16px_0px_rgba(0,0,0,0.05)] overflow-hidden flex flex-col snap-start hover:shadow-xl transition-shadow duration-300"
              >
                <div className="w-full h-[200px] overflow-hidden">
                  <img src={card.image} alt={card.title} className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-6 flex flex-col gap-2">
                  <div className="flex flex-wrap gap-2 mb-2">
                    {card.tags.map((tag, i) => (
                      <span
                        key={i}
                        className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded bg-[#E0F2FE] text-[#112140]`}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  <h3 className="text-[20px] font-bold text-[#112140]">{card.title}</h3>
                  <p className="text-[15px] text-[#475467] line-clamp-3">{card.description}</p>
                  <a
                    href="#"
                    className="text-[#2E90FA] text-[15px] font-semibold flex items-center mt-4 group w-fit"
                  >
                    Know More
                    <svg
                      className="ml-2 w-4 h-4 transform group-hover:translate-x-1 transition-transform"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <path
                        d="M4.16602 10H15.8327"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M10.834 5L15.834 10L10.834 15"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </a>
                </div>
              </div>
            ))}
          </div>

          {/* Partners and Certifications */}
          <div className="mt-12 pt-12 border-t border-[#E4E7EC]">
            <div className="text-center text-[#112140]/60 font-bold mb-8 tracking-[0.2em] text-[13px]">
              PARTNERS AND CERTIFICATIONS
            </div>
            <div className="flex flex-wrap justify-center items-center gap-8 sm:gap-16 opacity-60 hover:opacity-100 transition-opacity grayscale hover:grayscale-0 duration-500">
              <img src="/images/iso.png" alt="ISO Certification Badge" className="h-10 sm:h-14 object-contain" />
              <img src="/images/FSC.png" alt="FSC Certification Badge" className="h-10 sm:h-14 object-contain" />
              <img src="/images/GMP.png" alt="GMP Certification Badge" className="h-10 sm:h-14 object-contain" />
              <img src="/images/RoHS.png" alt="RoHS Certification Badge" className="h-10 sm:h-14 object-contain" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SustainabilityResultsSection;